import React, { useState, useEffect } from "react";
import { Menu, X, Sparkles, ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { NAV_ITEMS, LINKS } from "../mock";
import { useCountdown, scrollToId } from "../hooks/useReveal";

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-semibold tabular-nums text-[#f6e3a8] text-sm md:text-base leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">{label}</span>
    </div>
  );
}

export default function Navbar({ promo = true, switchLink = null }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { d, h, m, s } = useCountdown(7);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  const promoText = "✨ OFFRE SPÉCIALE · JUSQU'À -30% · PROMO 7 JOURS ✨";

  return (
    <>
      {promo && (
        <div className="relative z-[60] overflow-hidden bg-gradient-to-r from-[#0e3a4a] via-[#1f6f8b] to-[#0e3a4a] py-2 border-b border-white/10">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex">
                {Array.from({ length: 6 }).map((__, j) => (
                  <span key={j} className="mx-6 text-xs font-semibold tracking-[0.2em] text-[#f6e3a8]">
                    {promoText}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      )}

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#070d1c]/90 backdrop-blur-xl border-b border-white/10 py-2.5"
            : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between gap-4">
          <button onClick={() => go("#top")} className="flex items-center gap-2 group">
            <Sparkles className="w-5 h-5 text-[#f6e3a8] group-hover:rotate-12 transition-transform" />
            <span className="font-[Marcellus] tracking-wide text-[15px] md:text-base text-white">
              Maîtres Dauphins
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => go(item.href)}
                className="text-sm text-white/75 hover:text-[#f6e3a8] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#f6e3a8] after:transition-all hover:after:w-full"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {promo && (
              <div className="hidden md:flex items-center gap-2.5 px-3 py-1.5 rounded-full glass">
                <Unit value={d} label="J" />
                <span className="text-white/30">:</span>
                <Unit value={h} label="H" />
                <span className="text-white/30">:</span>
                <Unit value={m} label="M" />
                <span className="text-white/30">:</span>
                <Unit value={s} label="S" />
              </div>
            )}

            {switchLink && (
              <Link
                to={switchLink.to}
                className="hidden md:inline-flex items-center gap-1.5 btn-ghost rounded-full px-4 py-2 text-xs font-medium"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                {switchLink.label}
              </Link>
            )}

            <a
              href={LINKS.inscription}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex btn-gold rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              Je réserve ma place
            </a>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-white p-1.5" aria-label="menu">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#070d1c] border-white/10 text-white w-[85%] max-w-sm">
                <div className="flex items-center justify-between mb-8 mt-2">
                  <span className="font-[Marcellus] text-lg">Maîtres Dauphins</span>
                  <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
                </div>
                <div className="flex flex-col gap-1">
                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.href}
                      onClick={() => go(item.href)}
                      className="text-left py-3 px-3 rounded-lg text-white/80 hover:bg-white/5 hover:text-[#f6e3a8] transition-colors text-[15px]"
                    >
                      {item.label}
                    </button>
                  ))}
                  {switchLink && (
                    <Link
                      to={switchLink.to}
                      onClick={() => setOpen(false)}
                      className="text-left py-3 px-3 rounded-lg text-[#7fd8e8] hover:bg-white/5 transition-colors text-[15px] flex items-center gap-2"
                    >
                      <ArrowLeftRight className="w-4 h-4" /> {switchLink.label}
                    </Link>
                  )}
                </div>
                <a
                  href={LINKS.inscription}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold rounded-full px-5 py-3 text-sm font-semibold block text-center mt-8"
                >
                  Je réserve ma place
                </a>
                {promo && (
                  <div className="mt-6 flex items-center justify-center gap-2.5 px-3 py-3 rounded-xl glass">
                    <Unit value={d} label="Jours" />
                    <span className="text-white/30">:</span>
                    <Unit value={h} label="Heures" />
                    <span className="text-white/30">:</span>
                    <Unit value={m} label="Min" />
                    <span className="text-white/30">:</span>
                    <Unit value={s} label="Sec" />
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
}
